import { z } from 'zod';
import { EVENT_TYPE_NAMES, eventSchema } from './events';
import { ulidSchema } from './id';
import { publicProfileSchema } from './user';

/**
 * The live board frame — what a `/ws?boardId=<ulid>` socket actually receives.
 *
 * `FR-COLLAB-025`, item `BE-18.1`, block **B18**. Every other block in this
 * package declares the shape of an HTTP request or response; this is the first
 * file whose subject is a **push**, and the direction is what makes it unusual
 * rather than the shape.
 *
 * ---------------------------------------------------------------------------
 * THE FRAME IS THE EVENT. THERE IS NO ENVELOPE, AND THAT IS A DECISION
 * ---------------------------------------------------------------------------
 * `liveFrameSchema` **is** `eventSchema`, aliased — not a wrapper around it, not
 * a copy of its fields, not `z.object({ version, event })`. The obvious
 * alternative was an envelope carrying a protocol version and the event inside
 * it, and it was rejected for a reason that is about the tree rather than about
 * taste: **`apps/server/src/events/writer.ts` puts `JSON.stringify(event)` on
 * the wire and has done since B03**, and
 * `apps/server/src/ws/board-socket.integration.test.ts` asserts a bare event
 * shape arrives at a subscribed socket. An envelope would change what is on the
 * wire, so it would be a rewrite of the publisher and a breaking change to a
 * shipped, tested surface — bought with a version field nothing reads.
 *
 * ⚠ **WHAT THAT COSTS, RECORDED RATHER THAN GLOSSED: there is no protocol
 * version field, so there is no in-band way to evolve the frame format later.**
 * The escape hatch, if one is ever needed, is the same one the rest of this
 * product uses for a shape change — a new event *type* in `./events`, which the
 * closed `z.enum(EVENT_TYPE_NAMES)` union below makes a client-visible,
 * parse-time fact rather than a silent one. A future block that genuinely needs
 * a second frame *kind* (a server-originated hello, a presence tick) should add
 * it as a discriminated union **beside** this schema, not by widening it, for
 * exactly the reason `card-detail.ts` extends `cardSchema` on a copy: this
 * schema is what every board frame is validated against, and widening it to
 * admit a second kind makes every consumer's narrowing optional.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE ASYMMETRY THAT MATTERS MOST: THE SERVER DOES NOT PARSE AGAINST THIS
 * ---------------------------------------------------------------------------
 * **This schema is a contract for the CLIENT to parse against. The fan-out does
 * not use it, deliberately.** `board-socket.ts`'s Redis `message` handler
 * forwards the payload **string** to every socket in the room byte for byte; it
 * never `parse`s, never `safeParse`s, and never re-serialises. Three reasons, in
 * descending order of how badly the alternative fails:
 *
 *   1. **A validating fan-out is a fan-out that can drop a real event.** The
 *      publisher already validated on the way in (`eventInputSchema.parse` in
 *      `insertEvents`) and the row came back from Postgres; a second check on
 *      the read side can only ever *reject* something that already committed,
 *      and a dropped frame is invisible — no status code, no response body, no
 *      log line the client can see. `SC-1`'s stream is append-only precisely so
 *      that a change which happened is never unrecoverable.
 *   2. **It would break a shipped test, and the test is right.**
 *      `board-socket.integration.test.ts` publishes a deliberately PARTIAL
 *      frame — `{ id, type: 'skeleton.noop', boardId }`, no `cardId`, no
 *      `payload`, no `createdAt` — to prove the *transport* independently of any
 *      product event. `liveFrameSchema.safeParse` refuses that object (the
 *      missing keys are `nullable`, never `optional`), so a validating
 *      subscriber would drop it and redden a suite whose subject is Redis, not
 *      Zod. `./events` records that `skeleton.noop` is retained on purpose and
 *      may be deleted only together with that test's payload.
 *   3. **Cost.** Parsing every frame once per recipient, on the hot path of
 *      every mutation in the product, to learn something the publisher already
 *      knew.
 *
 * ⚠ ~~The **one** thing the subscriber *does* read out of the payload is `type`~~
 * — **two things, since B31: `type` and `cardId`** (corrected in place; the count
 * moved, the discipline did not). Both are read with a bare `JSON.parse` and a
 * string comparison — `type` against `ACCESS_REVALIDATION_EVENT_TYPES`, `cardId`
 * against a card-room member's own — never through this schema, for the same
 * reason in both cases: a frame it cannot parse must still be forwarded, and must
 * never take the fan-out down. A payload that is not JSON answers "not a
 * revocation" for the first and "names no card" for the second, and both answers
 * mean *forward*. See the card-room passage below.
 *
 * ---------------------------------------------------------------------------
 * DERIVED, NOT RESTATED — AND WHY THAT IS AN ALIAS RATHER THAN A REBUILD
 * ---------------------------------------------------------------------------
 * `export const liveFrameSchema = eventSchema` restates not one field, which is
 * this package's standing rule (`card.ts` imports
 * `CARD_TITLE_MAX_LENGTH` rather than writing a second 512;
 * `board-contents.ts` composes rather than copies). `z.object(eventSchema.shape)`
 * would also satisfy "no field restated" and was considered — the alias is
 * preferred because a rebuild silently loses anything `eventSchema` later gains
 * that is not a shape key (a `.refine`, a `.superRefine`, a catchall), and there
 * is no plausible future in which the frame wants to diverge from the event
 * while still being the event.
 *
 * The name earns its keep even so, and this is not an alias for its own sake:
 * `MoltEvent` is what a **row of `activity_entries`** is, and `LiveFrame` is
 * what a **socket receives**. They are the same shape today because B18 decided
 * they should be. A client importing `LiveFrame` is stating which of the two it
 * depends on, so the day the two do diverge, the diff lands on the consumers
 * that actually care rather than on every reader of the event vocabulary.
 *
 * ⚠ **`z.object`, therefore an unknown top-level key is STRIPPED, not refused.**
 * `eventSchema` is a plain `z.object` and this alias inherits that exactly —
 * `safeParse` on a frame carrying an extra key **succeeds**, with the key gone
 * from `parsed.data`. This is the trap `./notification.ts` names by name for its
 * own view schemas, and it is repeated here because it inverts the obvious test:
 * an assertion that the frame carries no envelope field must go through
 * `Object.keys(liveFrameSchema.shape)` or `Object.keys(parsed.data)`, because
 * `safeParse({ …, protocolVersion: 1 })` returning `success: true` says nothing
 * whatsoever about whether `protocolVersion` is part of the contract. The two
 * *input* schemas in this package that genuinely refuse unknown keys are
 * `z.strictObject`; a frame is not an input, and tightening this one would make
 * the server's own additive change a client-side parse failure.
 *
 * ---------------------------------------------------------------------------
 * WHAT A CLIENT MUST NOT INFER FROM A FRAME
 * ---------------------------------------------------------------------------
 * A frame says **that** something changed and **what kind** of change it was. It
 * is not a state delta and must not be applied as one — `payload` is
 * `Record<string, unknown>` by design and its contents are per-type, informal
 * and not part of any schema. `FR-COLLAB-025` AC4's "the board reconciles to
 * current state" is satisfied by refetching `GET /boards/:id/contents` (B13),
 * which is why **no replay or `?since=<eventId>` catch-up was built**: a client
 * that missed frames while disconnected reconciles by reading, not by
 * rewinding.
 *
 * ⚠ A frame is also **not** a notification. `notifications` rows (B17) are
 * per-recipient, are filtered by `FR-NOTIF-004` so that an actor never hears
 * about their own action, and are read over HTTP. A frame is per-**board**, is
 * fanned out to every subscribed socket with no per-recipient filter at all, and
 * therefore **echoes back to the account that caused it** — which `FE-18.1`
 * needs in order to reconcile against optimistic local state.
 *
 * ---------------------------------------------------------------------------
 * ⚠ B31 ADDED A CARD ROOM. IT **FILTERS** FRAMES; IT DOES NOT ENVELOPE OR
 * TRANSFORM THEM, AND THIS SCHEMA IS UNCHANGED
 * ---------------------------------------------------------------------------
 * `BE-31.2`, `FR-COLLAB-026`, `.b31-design.md` §3.7. `GET /ws` now takes an
 * **optional** `&cardId=<ulid>` beside `boardId`, and a socket that supplies it
 * receives the frames naming that card plus the frames naming no card, instead of
 * every frame on the board. **Nothing about a frame changed.**
 *
 * Specifically, and each of these is a thing a reader will expect to have moved:
 *
 *   - **`liveFrameSchema` is still `eventSchema`, aliased.** Not a field was
 *     added, removed or made optional. A card-room frame and a board-room frame
 *     are the same bytes; a client parses both against this one schema.
 *   - **There is still no protocol version, and B31 did not add one.** The "what
 *     that costs" note above stands exactly as written — a card filter is not a
 *     format change, so it needed no in-band way to announce itself. The client
 *     asks for the filter in the query string, which is out of band by
 *     construction.
 *   - **No envelope, no wrapper, no per-recipient content.** The server never
 *     re-serialises: `apps/server/src/ws/board-socket.ts` forwards the same
 *     payload **string** to a card-filtered member that it forwards to a
 *     board-room member. The only thing the filter can do to a frame is **not
 *     send it**.
 *   - **The echo two paragraphs up survives.** A card room is not the
 *     per-recipient filter this schema says a frame does not have: it is a filter
 *     the *subscriber* asked for, over a field (`cardId`) already on the frame,
 *     and it is blind to `actorUserId`. The writer's own card-back socket still
 *     receives its own frame — which is what `FE-31.1` reconciles against, and
 *     what `.b31-design.md` §3.5 relies on when it declines to widen any response
 *     schema for the override notice.
 *   - **The `null` arm.** A frame whose `cardId` is `null` — `board.deleted`,
 *     `board.visibility_changed`, `board.renamed`, the whole `list.*` family —
 *     reaches a card-room member too, and must. `shouldDeliverToCardMember` in
 *     `board-socket.ts` carries the argument in full; the short form is that a
 *     card back whose list was just deleted is told **only** by a frame that names
 *     no card, because `DELETE /lists/:id` soft-deletes every card in the list and
 *     emits nothing card-scoped at all.
 *
 * ⚠ So "a frame says *that* something changed, never enough to replay it" is
 * unaffected, and **no replay, `?since=`, presence or client→server subscribe
 * vocabulary was built** — §3.7 restates B18's refusal of all four, and there is
 * still no `'message'` handler on the socket. A card-room client reconciles by
 * reading `GET /cards/:id` (B14), the same remedy the paragraph above prescribes
 * for a board.
 */
export const liveFrameSchema = eventSchema;

export type LiveFrame = z.infer<typeof liveFrameSchema>;

/**
 * B61's server-originated live frames that are NOT `activity_entries` rows.
 *
 * Kept beside, not inside, `liveFrameSchema`: the persisted-event frame remains
 * the exact B18 alias above, while reaction changes and typing indicators are
 * transient socket facts. They still carry top-level `boardId` and `cardId` so
 * the existing board channel and B31 card-room filter can route them without
 * scanning payloads.
 */
export const liveReactionChangedFrameSchema = z.object({
  type: z.literal('comment.reactions_changed'),
  boardId: ulidSchema,
  cardId: ulidSchema,
  commentId: ulidSchema,
  actorUserId: ulidSchema,
});

export type LiveReactionChangedFrame = z.infer<typeof liveReactionChangedFrameSchema>;

export const liveTypingStateSchema = z.enum(['started', 'stopped']);

export type LiveTypingState = z.infer<typeof liveTypingStateSchema>;

export const liveTypingFrameSchema = z.object({
  type: z.literal('comment.typing'),
  boardId: ulidSchema,
  cardId: ulidSchema,
  user: publicProfileSchema,
  state: liveTypingStateSchema,
  expiresAt: z.string().min(1),
});

export type LiveTypingFrame = z.infer<typeof liveTypingFrameSchema>;

export const liveMirrorSourceChangedFrameSchema = z.object({
  type: z.literal('card.mirror_source_changed'),
  boardId: ulidSchema,
  cardId: ulidSchema,
  sourceBoardId: ulidSchema,
  activityEntryId: ulidSchema,
  sourceType: z.enum(EVENT_TYPE_NAMES),
});

export type LiveMirrorSourceChangedFrame = z.infer<
  typeof liveMirrorSourceChangedFrameSchema
>;

export const liveNotificationChangedFrameSchema = z.object({
  type: z.literal('notification.changed'),
});

export type LiveNotificationChangedFrame = z.infer<
  typeof liveNotificationChangedFrameSchema
>;

export const liveNonActivityFrameSchema = z.discriminatedUnion('type', [
  liveReactionChangedFrameSchema,
  liveTypingFrameSchema,
  liveMirrorSourceChangedFrameSchema,
]);

export type LiveNonActivityFrame = z.infer<typeof liveNonActivityFrameSchema>;

/**
 * The first client-to-server websocket message shape.
 *
 * It is strict and deliberately carries neither `boardId` nor `cardId`: typing
 * is accepted only on a socket already subscribed to `/ws?boardId=...&cardId=...`.
 * It also carries no draft text.
 */
export const liveClientTypingMessageSchema = z.strictObject({
  type: z.literal('comment.typing'),
  state: liveTypingStateSchema,
});

export type LiveClientTypingMessage = z.infer<typeof liveClientTypingMessageSchema>;

export const liveClientMessageSchema = liveClientTypingMessageSchema;

export type LiveClientMessage = z.infer<typeof liveClientMessageSchema>;
